/**
 * Omakase Vault - shared types
 * ------------------------------------------------------------------
 * Single source of truth for type definitions imported by BOTH the
 * SPA (apps/web) and the Cloudflare Pages Functions (functions/).
 *
 * Crypto values are BRANDED so the compiler refuses passing the wrong
 * sort of bytes (e.g. an IV where a ciphertext is expected). At runtime
 * branded types are plain Uint8Array - zero cost.
 */

// =============================================================================
// Branded primitives
// =============================================================================

declare const __brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [__brand]: B };

/** Random 12-byte AES-GCM initialization vector. */
export type IvBytes = Brand<Uint8Array, "IvBytes">;

/** AES-GCM ciphertext including the 16-byte authentication tag. */
export type CiphertextBytes = Brand<Uint8Array, "CiphertextBytes">;

/** AES-KW-256 wrapped key blob (40 bytes for a 32-byte CK). */
export type WrappedKeyBytes = Brand<Uint8Array, "WrappedKeyBytes">;

/** SHA-256 digest output (32 bytes). */
export type Sha256Bytes = Brand<Uint8Array, "Sha256Bytes">;

/** Argon2id salt (16 bytes). */
export type KdfSaltBytes = Brand<Uint8Array, "KdfSaltBytes">;

/** Symmetric 256-bit key (raw 32 bytes, NEVER stored at rest). */
export type RawSymmetricKey = Brand<Uint8Array, "RawSymmetricKey">;

/** Ed25519 signature (64 bytes). */
export type Ed25519Signature = Brand<Uint8Array, "Ed25519Signature">;

/** Ed25519 raw seed / private key (32 bytes, NEVER stored at rest). */
export type Ed25519RawPriv = Brand<Uint8Array, "Ed25519RawPriv">;

/** WebAuthn opaque credential id, server-stored. */
export type WebAuthnCredentialId = Brand<Uint8Array, "WebAuthnCredentialId">;

/** WebAuthn COSE-encoded public key. */
export type WebAuthnPublicKey = Brand<Uint8Array, "WebAuthnPublicKey">;

/**
 * Branded factory: wraps a raw Uint8Array into a typed bytes brand without
 * runtime cost. Use the dedicated helpers in @shared/crypto/bytes when you
 * need to construct bytes from sources (random, base64, hex).
 */
export function asBrand<B extends Uint8Array>(bytes: Uint8Array): B {
  return bytes as B;
}

// =============================================================================
// JWK strings (always stored as JSON.stringify of a JsonWebKey)
// =============================================================================

declare const __jwkBrand: unique symbol;
type JwkBrand<K extends string> = string & { readonly [__jwkBrand]: K };

/** ECDH P-256 public key in JWK form. */
export type EcdhPubJwk = JwkBrand<"EcdhPubJwk">;
/** ECDH P-256 private key in JWK form (NEVER stored unwrapped at rest). */
export type EcdhPrivJwk = JwkBrand<"EcdhPrivJwk">;
/** Ed25519 public key in JWK form. */
export type Ed25519PubJwk = JwkBrand<"Ed25519PubJwk">;

// =============================================================================
// IDs
// =============================================================================

declare const __idBrand: unique symbol;
type IdBrand<K extends string> = string & { readonly [__idBrand]: K };

export type UserId = IdBrand<"UserId">;
export type VaultItemId = IdBrand<"VaultItemId">;
export type CategoryId = IdBrand<"CategoryId">;
export type SessionId = IdBrand<"SessionId">;
export type DeviceId = IdBrand<"DeviceId">;
export type InviteId = IdBrand<"InviteId">;
export type AuditId = IdBrand<"AuditId">;

// =============================================================================
// Domain enums
// =============================================================================

export type Role = "SUPER_USER" | "ADMIN" | "EMPLOYEE";
export const ROLES = ["SUPER_USER", "ADMIN", "EMPLOYEE"] as const satisfies readonly Role[];

export type VaultScope = "personal" | "company";
export const VAULT_SCOPES = ["personal", "company"] as const satisfies readonly VaultScope[];

export type KdfAlgo = "argon2id" | "pbkdf2-sha256";

export type AuditAction =
  | "LOGIN" | "LOGOUT" | "LOGIN_FAIL"
  | "ITEM_CREATE" | "ITEM_UPDATE" | "ITEM_DELETE" | "ITEM_RESTORE"
  | "WRAP_ADD" | "WRAP_REMOVE"
  | "DEVICE_REGISTER" | "DEVICE_APPROVE" | "DEVICE_REVOKE"
  | "RESET_REQUEST" | "RESET_COMPLETE"
  | "PASSWORD_CHANGE"
  | "INVITE_CREATE" | "INVITE_CONSUME"
  | "TOTP_ENROLL" | "TOTP_VERIFY" | "TOTP_FAIL"
  | "RECOVERY_USE"
  | "SU_KEYPAIR_INIT";

// =============================================================================
// KDF parameters
// =============================================================================

export interface Argon2idParams {
  readonly algo: "argon2id";
  /** Memory size in KiB. OWASP 2024 baseline = 65536 (64 MiB). */
  readonly m: number;
  /** Time cost (iterations). OWASP 2024 baseline = 3. */
  readonly t: number;
  /** Parallelism. Browser tab = 1. */
  readonly p: number;
  /** Hash length in bytes. AES-256 needs 32. */
  readonly hashLength: number;
}

export interface Pbkdf2Params {
  readonly algo: "pbkdf2-sha256";
  readonly iterations: number;
  readonly hashLength: number;
}

export type KdfParams = Argon2idParams | Pbkdf2Params;

export const DEFAULT_ARGON2ID_PARAMS: Argon2idParams = Object.freeze({
  algo: "argon2id",
  m: 65536,
  t: 3,
  p: 1,
  hashLength: 32,
});

// =============================================================================
// AAD context for vault item AES-GCM
// =============================================================================

/** AAD = item_id || ":" || version || ":" || scope */
export interface VaultItemAadContext {
  readonly item_id: VaultItemId;
  readonly version: number;
  readonly scope: VaultScope;
}

// =============================================================================
// Database row shapes
// =============================================================================

export interface UserRow {
  readonly id: UserId;
  readonly email: string;
  readonly personal_email: string | null;
  readonly name: string;
  readonly role: Role;
  readonly kdf_algo: KdfAlgo;
  readonly kdf_salt: KdfSaltBytes;
  readonly kdf_params: string;
  readonly kdf_extra_input: Uint8Array | null;
  readonly ecdh_pub_jwk: EcdhPubJwk;
  readonly wrapped_priv: CiphertextBytes;
  readonly wrapped_ask: CiphertextBytes;
  readonly wrapped_metadata_key: CiphertextBytes;
  readonly signing_pub_jwk: Ed25519PubJwk | null;
  readonly pubkey_sig: Ed25519Signature | null;
  /** AES-GCM(ASK, raw Ed25519 32B seed). Required for SUPER_USER, NULL otherwise. */
  readonly wrapped_signing_priv: CiphertextBytes | null;
  readonly totp_secret_ct: CiphertextBytes | null;
  readonly totp_enrolled_at: number | null;
  /**
   * Server-side master password verifier.
   *   HKDF-SHA256(ikm=KEK, salt=kdf_salt, info="ovault-server-verifier-v1", len=32)
   * Compared constant-time on login. Independent of the ASK wrapping
   * derivation so leaking this hash does not weaken the at-rest key
   * envelope. Added in migration 0003. Nullable in SQL for legacy rows,
   * but the application requires non-NULL on every INSERT.
   */
  readonly master_password_hash: Sha256Bytes | null;
  readonly must_change_pw: 0 | 1;
  readonly reset_pending: 0 | 1;
  /** When 1, login is refused with account_locked even if credentials match. Set/cleared by SU/Admin via /api/users/:id/block. */
  readonly is_blocked: 0 | 1;
  readonly created_at: number;
  readonly updated_at: number;
  readonly deleted_at: number | null;
}

export interface VaultItemRow {
  readonly id: VaultItemId;
  readonly category_id: CategoryId;
  readonly scope: VaultScope;
  readonly owner_id: UserId | null;
  readonly name_ct: CiphertextBytes;
  readonly name_iv: IvBytes;
  readonly ciphertext: CiphertextBytes;
  readonly iv: IvBytes;
  readonly version: number;
  readonly created_by: UserId;
  readonly updated_by: UserId;
  readonly created_at: number;
  readonly updated_at: number;
  readonly deleted_at: number | null;
}

export interface WrappedKeyRow {
  readonly item_id: VaultItemId;
  readonly recipient_id: UserId;
  readonly ephemeral_pub_jwk: EcdhPubJwk;
  readonly wrapped_ck: WrappedKeyBytes;
  readonly created_at: number;
}

export interface CategoryRow {
  readonly id: CategoryId;
  readonly slug: string;
  readonly label_en: string;
  readonly label_jp: string;
  readonly icon_name: string;
  readonly default_brand_color: string;
  readonly order_index: number;
  readonly created_at: number;
}

export interface SessionRow {
  readonly id: SessionId;
  readonly user_id: UserId;
  readonly token_hash: Sha256Bytes;
  readonly device_id: DeviceId | null;
  readonly ip: string | null;
  readonly ua: string | null;
  readonly created_at: number;
  readonly last_used_at: number;
  readonly expires_at: number;
  readonly revoked_at: number | null;
}

export interface TrustedDeviceRow {
  readonly id: DeviceId;
  readonly user_id: UserId;
  readonly credential_id: WebAuthnCredentialId;
  readonly public_key: WebAuthnPublicKey;
  readonly sign_count: number;
  readonly device_label: string | null;
  /** Human-editable display name set by user/SU; preferred over device_label. */
  readonly device_name: string | null;
  readonly user_agent: string | null;
  readonly approved_at: number | null;
  readonly approved_by: UserId | null;
  readonly last_used_at: number | null;
  readonly created_at: number;
  readonly revoked_at: number | null;
}

export interface PendingInviteRow {
  readonly id: InviteId;
  readonly email: string;
  readonly name: string;
  readonly personal_email: string;
  readonly role: Exclude<Role, "SUPER_USER">;
  readonly items_ct: CiphertextBytes | null;
  readonly items_iv: IvBytes | null;
  readonly created_by: UserId;
  readonly created_at: number;
  readonly expires_at: number;
  readonly consumed_at: number | null;
}

export interface VaultItemHistoryRow {
  readonly id: string;
  readonly item_id: VaultItemId;
  readonly version: number;
  readonly ciphertext: CiphertextBytes;
  readonly iv: IvBytes;
  readonly name_ct: CiphertextBytes;
  readonly name_iv: IvBytes;
  readonly ct_sha256: Sha256Bytes;
  readonly actor_id: UserId;
  readonly created_at: number;
}

/**
 * Descriptor of a recipient as delivered by GET /api/recipients/company.
 * pubkey_sig is the Ed25519 signature by the SU over `id || ecdh_pub_jwk || role`.
 * The client MUST verify pubkey_sig before wrapping any CK to this recipient.
 */
export interface RecipientDescriptor {
  readonly user_id: UserId;
  readonly role: Role;
  readonly ecdh_pub_jwk: EcdhPubJwk;
  readonly pubkey_sig: Ed25519Signature;
}

// =============================================================================
// Re-exports of value schema
// =============================================================================

export type {
  VaultItemValue,
  KvRow,
  CustomField,
  VaultItemLogo,
  LucideIconName,
  VaultItemValueVersion,
} from "./vault-item-value.js";

export {
  CATEGORIES,
  CATEGORY_BY_SLUG,
  getCategoryBySlug,
  type CategoryDef,
} from "./vault-categories.js";
